// Zustand der Erwähnungs-Auswahl im Composer: Anfrage am Cursor, gefilterte bzw. geladene Einträge,
// aktiver Eintrag, Tastatur (↑/↓, Enter/Tab übernimmt, Esc schließt).
import { useState, type KeyboardEvent } from 'react';
import type { MentionItem } from '../MentionPopover';
import { orderMentions } from '../MentionPopover';
import { filterMentions, findMentionQuery, insertMention, type MentionMatch } from './mention';
import { useAsyncOptions, type OptionsLoader } from './useAsyncOptions';

export interface MentionSource {
  items?: MentionItem[];
  load?: OptionsLoader<MentionItem>;
}

export function useMentions(
  source: MentionSource | undefined,
  setText: (text: string, caret: number) => void,
) {
  const [match, setMatch] = useState<MentionMatch | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const open = Boolean(source && match);
  const async = useAsyncOptions(source?.load, match?.query ?? '', open);
  const items =
    !source || !match
      ? []
      : orderMentions(
          source.load ? async.results : filterMentions(source.items ?? [], match.query),
        );

  /** Nach jeder Eingabe bzw. Cursorbewegung aufrufen. */
  const update = (text: string, caret: number) => {
    if (!source) return;
    const next = findMentionQuery(text, caret);
    if (next?.query !== match?.query || next?.start !== match?.start) {
      setActiveIndex(0);
      if (next) async.markLoading();
    }
    setMatch(next);
  };

  const select = (item: MentionItem, text: string) => {
    if (!match) return;
    const result = insertMention(text, match, item.label);
    setMatch(null);
    setText(result.text, result.caret);
  };

  /** true, wenn die Taste von der Auswahl verbraucht wurde. */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>, text: string): boolean => {
    if (!open) return false;
    if (event.key === 'Escape') {
      setMatch(null);
      return true;
    }
    if (!items.length) return false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => (i + delta + items.length) % items.length);
      return true;
    }
    if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
      const item = items[Math.min(activeIndex, items.length - 1)];
      if (item) select(item, text);
      return true;
    }
    return false;
  };

  return {
    open,
    items,
    activeIndex: Math.min(activeIndex, Math.max(0, items.length - 1)),
    setActiveIndex,
    loading: async.loading,
    update,
    select,
    onKeyDown,
    close: () => setMatch(null),
  };
}
